import { useMemo, useState, type FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api, BladApi } from '../api/client';
import { useLista, type WynikListy } from '../api/lista';
import type {
  CelWydania, Dokument, Dostawca, Egzemplarz, Indeks, Lista, Lokalizacja, Partia,
  TypDokumentu, WynikSkanu, Zajecia,
} from '../api/typy';
import { useAuth } from '../auth/AuthContext';
import {
  AtrapaLeku, Karta, Komunikat, Pole, PoleTekstowe, Przelacznik, Przycisk, Wybor,
} from '../components/ui';
import { TYP_DOKUMENTU, UPRAWNIENIE_DOKUMENTU } from '../lib/slowniki';
import { data as fData, godzina } from '../lib/format';

interface Wiersz {
  klucz: string;
  itemId: string;
  nazwa: string;
  jednostka: string;
  atrapa: boolean;
  partiowany: boolean;
  sprzet: boolean;
  qty: string;
  locationId: string;
  locationToId: string;
  batchId: string;
  equipmentUnitId: string;
  notes: string;
  dopuscPoTerminie: boolean;
  powodPoTerminie: string;
}

const TYPY: TypDokumentu[] = ['PZ', 'WZ', 'MM', 'KOR', 'LIK'];

function pustyWiersz(): Wiersz {
  return {
    klucz: crypto.randomUUID(), itemId: '', nazwa: '', jednostka: '', atrapa: false,
    partiowany: false, sprzet: false, qty: '', locationId: '', locationToId: '',
    batchId: '', equipmentUnitId: '', notes: '', dopuscPoTerminie: false, powodPoTerminie: '',
  };
}

export function NowyDokument() {
  const { maUprawnienie } = useAuth();
  const nawiguj = useNavigate();
  const qc = useQueryClient();
  const [parametry] = useSearchParams();

  const dostepneTypy = TYPY.filter((t) => maUprawnienie(UPRAWNIENIE_DOKUMENTU[t]));
  const typZAdresu = parametry.get('typ') as TypDokumentu | null;
  const [typ, setTyp] = useState<TypDokumentu>(
    typZAdresu && dostepneTypy.includes(typZAdresu) ? typZAdresu : dostepneTypy[0] ?? 'PZ',
  );

  const [naglowek, setNaglowek] = useState({
    supplierId: '', classId: '', issuePurposeId: '', recipientNote: '', reason: '', notes: '',
  });
  const [wiersze, setWiersze] = useState<Wiersz[]>([pustyWiersz()]);
  const [kodSkanu, setKodSkanu] = useState('');
  const [blad, setBlad] = useState<string | null>(null);
  const [bledy, setBledy] = useState<Record<string, string>>({});
  const [ostrzezenieSkanu, setOstrzezenieSkanu] = useState<string | null>(null);

  const kartoteka = useLista({
    queryKey: ['kartoteka', 'do-dokumentu'],
    queryFn: () => api.get<Lista<Indeks>>('/kartoteka?tylkoAktywne=true&naStronie=500'),
  });
  const lokalizacje = useLista({
    queryKey: ['lokalizacje', 'skladowe'],
    queryFn: () => api.get<{ pozycje: Lokalizacja[] }>('/lokalizacje?tylkoAktywne=true'),
  });
  const dostawcy = useLista({
    queryKey: ['dostawcy'], queryFn: () => api.get<{ pozycje: Dostawca[] }>('/dostawcy'),
  });
  const cele = useLista({
    queryKey: ['cele-wydania'],
    queryFn: () => api.get<{ pozycje: CelWydania[] }>('/cele-wydania'),
  });
  const zajecia = useLista({
    queryKey: ['zajecia', 'do-wydania'],
    queryFn: () => api.get<Lista<Zajecia>>(
      `/zajecia?od=${new Date(Date.now() - 86_400_000).toISOString()}&rosnaco=true&naStronie=100`,
    ),
    enabled: typ === 'WZ',
  });

  const indeksy = useMemo(
    () => new Map(kartoteka.pozycje.map((i) => [String(i.id), i])),
    [kartoteka.pozycje],
  );

  function ustawWiersz(klucz: string, zmiany: Partial<Wiersz>) {
    setWiersze((w) => w.map((x) => (x.klucz === klucz ? { ...x, ...zmiany } : x)));
  }

  function wybierzIndeks(klucz: string, itemId: string) {
    const indeks = indeksy.get(itemId);
    ustawWiersz(klucz, {
      itemId,
      nazwa: indeks?.name ?? '',
      jednostka: indeks?.unit ?? '',
      atrapa: indeks?.isDrugDummy ?? false,
      partiowany: indeks?.tracksBatches ?? false,
      sprzet: indeks?.type === 'EQUIPMENT',
      batchId: '', equipmentUnitId: '',
      locationId: indeks?.defaultLocation ? String(indeks.defaultLocation.id) : '',
    });
  }

  /**
   * Skaner USB: kod + Enter dokłada pozycję do dokumentu.
   *
   * Materiał medyczny nosi kod GS1 — jeden odczyt niesie GTIN, serię
   * i termin ważności naraz. Gdy skan to potwierdzi (`zeskanowanaPartia`),
   * wiersz dostaje od razu właściwą partię, zamiast zmuszać do przepisania
   * z pudełka na osobnym ekranie.
   */
  async function skanuj(e: FormEvent) {
    e.preventDefault();
    // Po pierwszym `await` zdarzenie traci `currentTarget`, więc pole łapiemy teraz.
    const pole = e.currentTarget.querySelector('input');
    const kod = kodSkanu.trim();
    if (!kod) return;
    setOstrzezenieSkanu(null);
    try {
      const indeks = await api.get<WynikSkanu>(`/kartoteka/skan/${encodeURIComponent(kod)}`);

      let batchId = '';
      if (indeks.zeskanowanaPartia?.lotNumber) {
        const { lotNumber, expiryDate } = indeks.zeskanowanaPartia;
        // Partię ZAKŁADA wyłącznie przyjęcie. Przy wydaniu, przesunięciu,
        // likwidacji i korekcie zeskanowana seria musi już być w systemie —
        // inaczej powstawałyby puste partie-sieroty, a wiersz wydawałby towar
        // z serii, której nigdy nie przyjęto.
        let partia: Partia;
        if (typ === 'PZ') {
          partia = await api.post<Partia>('/partie/ze-skanu', { itemId: indeks.id, lotNumber, expiryDate });
        } else {
          const zapytanie = new URLSearchParams({ itemId: String(indeks.id), lotNumber });
          if (expiryDate) zapytanie.set('expiryDate', expiryDate);
          partia = await api.get<Partia>(`/partie/ze-skanu?${zapytanie}`);
        }
        batchId = String(partia.id);
        // Świeżo założona albo dobrana partia ma się pojawić na liście wyboru tej pozycji.
        void qc.invalidateQueries({ queryKey: ['partie', 'do-dokumentu', String(indeks.id)] });
      }

      // Dla pozycji prowadzącej partie ten sam indeks z INNĄ partią to osobna
      // linia — inaczej dwie różne serie zlałyby się w jedną ilość.
      const istniejacy = wiersze.find((w) =>
        w.itemId === String(indeks.id) && (!indeks.tracksBatches || w.batchId === batchId));
      if (istniejacy && !istniejacy.sprzet) {
        // Powtórny skan tej samej pozycji (tej samej partii) zwiększa ilość —
        // tak działa czytnik przy kompletowaniu.
        ustawWiersz(istniejacy.klucz, { qty: String((Number(istniejacy.qty) || 0) + 1) });
      } else {
        const pusty = wiersze.find((w) => !w.itemId);
        const klucz = pusty?.klucz ?? crypto.randomUUID();
        if (!pusty) setWiersze((w) => [...w, { ...pustyWiersz(), klucz }]);
        setTimeout(() => {
          wybierzIndeks(klucz, String(indeks.id));
          ustawWiersz(klucz, { qty: '1', batchId });
        }, 0);
      }
      setKodSkanu('');
    } catch (err) {
      setOstrzezenieSkanu(err instanceof BladApi ? err.message : 'Nie znaleziono pozycji.');
      // Nieudany kod zostaje w polu, żeby było widać, co się zeskanowało — ale
      // zaznaczony: następny odczyt czytnika USB go nadpisze, zamiast dokleić się
      // do starego kodu i wywołać kolejny, bezsensowny błąd.
      pole?.focus();
      pole?.select();
    }
  }

  const wystaw = useMutation({
    mutationFn: () =>
      api.post<Dokument>('/dokumenty', {
        type: typ,
        supplierId: typ === 'PZ' ? naglowek.supplierId || null : null,
        classId: typ === 'WZ' ? naglowek.classId || null : null,
        issuePurposeId: typ === 'WZ' && !naglowek.classId ? naglowek.issuePurposeId || null : null,
        recipientNote: naglowek.recipientNote,
        reason: naglowek.reason,
        notes: naglowek.notes,
        pozycje: wiersze
          .filter((w) => w.itemId && w.qty)
          .map((w) => ({
            itemId: Number(w.itemId),
            qty: Number(w.qty.replace(',', '.')),
            locationId: w.locationId || null,
            locationToId: typ === 'MM' ? w.locationToId || null : null,
            batchId: w.batchId || null,
            equipmentUnitId: w.equipmentUnitId || null,
            notes: w.notes,
            dopuscPoTerminie: w.dopuscPoTerminie,
            powodPoTerminie: w.powodPoTerminie,
          })),
      }),
    onSuccess: (dokument) => {
      void qc.invalidateQueries({ queryKey: ['stany'] });
      void qc.invalidateQueries({ queryKey: ['lokalizacja'] });
      void qc.invalidateQueries({ queryKey: ['dokumenty'] });
      nawiguj(`/dokumenty?otwarty=${dokument.id}`);
    },
    onError: (e) => {
      if (e instanceof BladApi) {
        setBledy(e.bledyPol);
        setBlad(Object.keys(e.bledyPol).length === 0 ? e.message : null);
        if (Object.keys(e.bledyPol).length > 0 && !e.message.includes('Formularz')) setBlad(e.message);
      } else {
        setBlad('Nie udało się wystawić dokumentu.');
      }
    },
  });

  if (dostepneTypy.length === 0) {
    return (
      <Komunikat rodzaj="uwaga" tytul="Brak uprawnień">
        Twoja rola nie pozwala wystawiać dokumentów magazynowych.
      </Komunikat>
    );
  }

  const opis = TYP_DOKUMENTU[typ];
  const wymagaPowodu = typ === 'KOR' || typ === 'LIK';

  return (
    <div className="flex flex-col gap-5">
      <div>
        <h1 className="text-xl font-semibold text-kamien-900">Nowy dokument</h1>
        <p className="mt-1 text-sm text-kamien-500">
          Dokument księguje się od razu przy zatwierdzeniu. Pomyłkę poprawia się stornem, nie edycją.
        </p>
      </div>

      {blad && <Komunikat rodzaj="blad">{blad}</Komunikat>}

      <Karta tytul="Nagłówek" opis={opis.opis}>
        <div className="grid gap-4 px-5 py-5 sm:grid-cols-2 lg:grid-cols-3">
          <Wybor etykieta="Rodzaj dokumentu" value={typ}
            onChange={(e) => { setTyp(e.target.value as TypDokumentu); setBledy({}); setBlad(null); }}>
            {dostepneTypy.map((t) => (
              <option key={t} value={t}>{t} — {TYP_DOKUMENTU[t].nazwa}</option>
            ))}
          </Wybor>

          {typ === 'PZ' && (
            <Wybor etykieta="Dostawca" value={naglowek.supplierId} blad={bledy.supplierId ?? dostawcy.blad}
              onChange={(e) => setNaglowek({ ...naglowek, supplierId: e.target.value })}>
              <option value="">— nie wskazano —</option>
              {dostawcy.pozycje.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
            </Wybor>
          )}

          {typ === 'WZ' && (
            <>
              {/* Odbiorca wydania: zajęcia ALBO inny cel — nigdy oba naraz. */}
              <div className="sm:col-span-2 lg:col-span-2">
                <Wybor etykieta="Na zajęcia" value={naglowek.classId} blad={bledy.classId ?? zajecia.blad}
                  onChange={(e) => setNaglowek({ ...naglowek, classId: e.target.value, issuePurposeId: '' })}>
                  <option value="">— wydanie poza dydaktyką —</option>
                  {zajecia.pozycje.map((z) => (
                    <option key={z.id} value={z.id}>
                      {fData(z.startsAt)} {godzina(z.startsAt)} · {z.course.name}
                      {z.groupName ? ` · ${z.groupName}` : ''} · {z.instructorName}
                    </option>
                  ))}
                </Wybor>
              </div>
              {!naglowek.classId && (
                <>
                  <Wybor etykieta="Cel wydania" value={naglowek.issuePurposeId}
                    blad={bledy.issuePurposeId ?? cele.blad}
                    onChange={(e) => setNaglowek({ ...naglowek, issuePurposeId: e.target.value })} required>
                    <option value="">— wybierz —</option>
                    {cele.pozycje.filter((c) => c.isActive).map((c) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </Wybor>
                  <Pole etykieta="Komu / na co konkretnie" value={naglowek.recipientNote}
                    blad={bledy.recipientNote}
                    onChange={(e) => setNaglowek({ ...naglowek, recipientNote: e.target.value })} />
                </>
              )}
            </>
          )}

          {wymagaPowodu && (
            <div className="sm:col-span-2 lg:col-span-3">
              <Pole etykieta="Uzasadnienie" value={naglowek.reason} blad={bledy.reason}
                onChange={(e) => setNaglowek({ ...naglowek, reason: e.target.value })}
                podpowiedz="Bez tego nie da się później odtworzyć, skąd wzięła się różnica" required />
            </div>
          )}

          <div className="sm:col-span-2 lg:col-span-3">
            <PoleTekstowe etykieta="Uwagi" value={naglowek.notes}
              onChange={(e) => setNaglowek({ ...naglowek, notes: e.target.value })} />
          </div>
        </div>
      </Karta>

      <Karta tytul="Pozycje" opis="Zeskanuj kod albo wybierz z listy">
        <div className="border-b border-kamien-200 px-5 py-4">
          <form onSubmit={skanuj} className="max-w-md">
            <Pole
              etykieta="Skanuj kod"
              value={kodSkanu}
              onChange={(e) => { setKodSkanu(e.target.value); setOstrzezenieSkanu(null); }}
              placeholder="Kod kreskowy albo kod indeksu"
              podpowiedz="Ustaw kursor tutaj i użyj czytnika — Enter dokłada pozycję"
              autoFocus
            />
          </form>
          {ostrzezenieSkanu && (
            <p className="mt-2 text-sm text-status-brak">{ostrzezenieSkanu}</p>
          )}
        </div>

        <div className="flex flex-col divide-y divide-kamien-200">
          {wiersze.map((w, i) => (
            <WierszPozycji
              key={w.klucz}
              wiersz={w}
              numer={i + 1}
              typ={typ}
              indeksy={kartoteka}
              lokalizacje={lokalizacje}
              bledy={bledy}
              wybierzIndeks={(id) => wybierzIndeks(w.klucz, id)}
              ustaw={(zmiany) => ustawWiersz(w.klucz, zmiany)}
              usun={() => setWiersze((lista) => lista.length > 1
                ? lista.filter((x) => x.klucz !== w.klucz)
                : [pustyWiersz()])}
            />
          ))}
        </div>

        <div className="flex flex-wrap gap-3 border-t border-kamien-200 px-5 py-4">
          <Przycisk onClick={() => setWiersze((w) => [...w, pustyWiersz()])}>
            Dodaj pozycję
          </Przycisk>
          <Przycisk
            wariant="glowny"
            disabled={wystaw.isPending || !wiersze.some((w) => w.itemId && w.qty)}
            onClick={() => { setBlad(null); setBledy({}); wystaw.mutate(); }}
          >
            {wystaw.isPending ? 'Księgowanie...' : `Zatwierdź ${typ}`}
          </Przycisk>
          <Przycisk wariant="cichy" onClick={() => nawiguj('/dokumenty')}>Anuluj</Przycisk>
        </div>
      </Karta>
    </div>
  );
}

function WierszPozycji({ wiersz, numer, typ, indeksy, lokalizacje, bledy, wybierzIndeks, ustaw, usun }: {
  wiersz: Wiersz;
  numer: number;
  typ: TypDokumentu;
  indeksy: WynikListy<Indeks>;
  lokalizacje: WynikListy<Lokalizacja>;
  bledy: Record<string, string>;
  wybierzIndeks: (id: string) => void;
  ustaw: (zmiany: Partial<Wiersz>) => void;
  usun: () => void;
}) {
  const prefiks = `pozycje.${numer - 1}`;

  const partie = useLista({
    queryKey: ['partie', 'do-dokumentu', wiersz.itemId],
    queryFn: () => api.get<Lista<Partia>>(`/partie?itemId=${wiersz.itemId}&naStronie=100`),
    enabled: Boolean(wiersz.itemId) && wiersz.partiowany,
  });

  const egzemplarze = useLista({
    queryKey: ['sprzet', 'do-dokumentu', wiersz.itemId],
    queryFn: () => api.get<Lista<Egzemplarz>>(`/sprzet?itemId=${wiersz.itemId}&naStronie=200`),
    enabled: Boolean(wiersz.itemId) && wiersz.sprzet,
  });

  return (
    <div className="grid gap-3 px-5 py-4 sm:grid-cols-2 lg:grid-cols-4">
      <div className="lg:col-span-2">
        <Wybor etykieta={`Pozycja ${numer}`} value={wiersz.itemId}
          blad={bledy[`${prefiks}.itemId`] ?? indeksy.blad}
          onChange={(e) => wybierzIndeks(e.target.value)}>
          <option value="">— wybierz z kartoteki —</option>
          {indeksy.pozycje.map((i) => (
            <option key={i.id} value={i.id}>
              {i.code} — {i.name}{i.isDrugDummy ? ' (atrapa leku)' : ''}
            </option>
          ))}
        </Wybor>
        {wiersz.atrapa && <div className="mt-1.5"><AtrapaLeku /></div>}
      </div>

      <Pole
        etykieta={`Ilość${wiersz.jednostka ? ` (${wiersz.jednostka})` : ''}`}
        value={wiersz.qty} blad={bledy[`${prefiks}.qty`]}
        onChange={(e) => ustaw({ qty: e.target.value })}
        inputMode="decimal"
        podpowiedz={typ === 'KOR' ? 'Ze znakiem: −5 zdejmuje, +5 dokłada' : undefined}
      />

      <Wybor etykieta={typ === 'MM' ? 'Z lokalizacji' : 'Lokalizacja'}
        value={wiersz.locationId} blad={bledy[`${prefiks}.locationId`] ?? lokalizacje.blad}
        onChange={(e) => ustaw({ locationId: e.target.value })}>
        <option value="">— wybierz —</option>
        {lokalizacje.pozycje.map((l) => (
          <option key={l.id} value={l.id}>{l.code} — {l.path ?? l.name}</option>
        ))}
      </Wybor>

      {typ === 'MM' && (
        <Wybor etykieta="Do lokalizacji" value={wiersz.locationToId}
          blad={bledy[`${prefiks}.locationToId`] ?? lokalizacje.blad}
          onChange={(e) => ustaw({ locationToId: e.target.value })}>
          <option value="">— wybierz —</option>
          {lokalizacje.pozycje.map((l) => (
            <option key={l.id} value={l.id}>{l.code} — {l.path ?? l.name}</option>
          ))}
        </Wybor>
      )}

      {wiersz.partiowany && (
        <div className="lg:col-span-2">
          <Wybor
            etykieta="Partia"
            value={wiersz.batchId} blad={bledy[`${prefiks}.batchId`] ?? partie.blad}
            onChange={(e) => ustaw({ batchId: e.target.value })}>
            <option value="">
              {typ === 'PZ' ? '— wskaż serię —' : '— dobierz automatycznie (FEFO) —'}
            </option>
            {partie.pozycje.map((p) => (
              <option key={p.id} value={p.id}>
                {p.lotNumber ?? `partia ${p.id}`}
                {p.expiryDate ? ` — termin ${fData(p.expiryDate)}` : ' — bez terminu'}
              </option>
            ))}
          </Wybor>
        </div>
      )}

      {wiersz.sprzet && (
        <div className="lg:col-span-2">
          <Wybor etykieta="Egzemplarz" value={wiersz.equipmentUnitId}
            blad={bledy[`${prefiks}.equipmentUnitId`] ?? egzemplarze.blad}
            onChange={(e) => ustaw({ equipmentUnitId: e.target.value, qty: '1' })}>
            <option value="">— wybierz egzemplarz —</option>
            {egzemplarze.pozycje.map((e) => (
              <option key={e.id} value={e.id}>
                {e.nrCsm}{e.model ? ` — ${e.model}` : ''}{e.serialNumber ? ` (s/n ${e.serialNumber})` : ''}
              </option>
            ))}
          </Wybor>
        </div>
      )}

      {(typ === 'WZ' || typ === 'LIK') && wiersz.partiowany && (
        <div className="lg:col-span-4">
          <Przelacznik
            etykieta="Dopuść wydanie po terminie ważności"
            opis="Wymaga uprawnień kierownika. Decyzja trafia do dziennika."
            checked={wiersz.dopuscPoTerminie}
            onChange={(e) => ustaw({ dopuscPoTerminie: e.target.checked })}
          />
          {wiersz.dopuscPoTerminie && (
            <Pole etykieta="Powód wydania po terminie" value={wiersz.powodPoTerminie}
              blad={bledy[`${prefiks}.powodPoTerminie`]}
              onChange={(e) => ustaw({ powodPoTerminie: e.target.value })} />
          )}
        </div>
      )}

      <div className="lg:col-span-3">
        <Pole etykieta="Uwagi do pozycji" value={wiersz.notes}
          onChange={(e) => ustaw({ notes: e.target.value })} />
      </div>
      <div className="flex items-end">
        <Przycisk wariant="cichy" className="min-h-9 text-xs" onClick={usun}>Usuń pozycję</Przycisk>
      </div>
    </div>
  );
}
